import { useState } from "react";
import CaptchaWidget from "./CaptchaWidget";
import { MAX_NOTE_LENGTH, hasErrors, validateRsvpForm } from "../utils/validation";
import api, { ApiError } from "../utils/api";

const INITIAL_VALUES = {
  parentFirstName: "",
  parentLastInitial: "",
  parentEmail: "",
  mobile: "",
  teenCount: 1,
  gradeRange: "",
  note: "",
  isAdult: false,
  agreesToConduct: false,
  agreesToPrivacy: false,
  agreesToTerms: false,
  website: "", // honeypot
};

export default function RsvpForm({ eventId, onSuccess }) {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  function updateField(field, value) {
    setValues((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);

    if (values.website) {
      // Honeypot triggered; silently drop without revealing the check to a bot.
      return;
    }

    const validationErrors = validateRsvpForm(values);
    if (!captchaToken) {
      validationErrors.captcha = "Please complete the verification check.";
    }
    setErrors(validationErrors);
    if (hasErrors(validationErrors)) return;

    setSubmitting(true);
    try {
      await api.createRsvp(eventId, { ...values, teenCount: Number(values.teenCount), captchaToken });
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.fields) {
        setErrors((prev) => ({ ...prev, ...err.fields }));
      }
      setSubmitError(err instanceof ApiError ? err.message : "Unable to submit your RSVP. Please try again.");
    } finally {
      setCaptchaToken("");
      setCaptchaReset((value) => value + 1);
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-describedby="rsvp-form-instructions">
      <p id="rsvp-form-instructions">
        Fields marked with <span aria-hidden="true">*</span>
        <span className="visually-hidden">required</span> are required.
      </p>

      <div className="form-field">
        <label htmlFor="parentFirstName">Parent/guardian first name *</label>
        <input
          id="parentFirstName"
          type="text"
          required
          value={values.parentFirstName}
          onChange={(e) => updateField("parentFirstName", e.target.value)}
          aria-invalid={Boolean(errors.parentFirstName)}
          aria-describedby={errors.parentFirstName ? "parentFirstName-error" : undefined}
        />
        {errors.parentFirstName && (
          <p id="parentFirstName-error" className="field-error">
            {errors.parentFirstName}
          </p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor="parentLastInitial">Parent/guardian last initial *</label>
        <input
          id="parentLastInitial"
          type="text"
          maxLength={2}
          required
          value={values.parentLastInitial}
          onChange={(e) => updateField("parentLastInitial", e.target.value)}
          aria-invalid={Boolean(errors.parentLastInitial)}
          aria-describedby={errors.parentLastInitial ? "parentLastInitial-error" : undefined}
        />
        {errors.parentLastInitial && (
          <p id="parentLastInitial-error" className="field-error">
            {errors.parentLastInitial}
          </p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor="parentEmail">Email address *</label>
        <input
          id="parentEmail"
          type="email"
          required
          value={values.parentEmail}
          onChange={(e) => updateField("parentEmail", e.target.value)}
          aria-invalid={Boolean(errors.parentEmail)}
          aria-describedby={errors.parentEmail ? "parentEmail-error" : "parentEmail-hint"}
        />
        <p id="parentEmail-hint" className="field-hint">
          We&rsquo;ll send a confirmation link to this address.
        </p>
        {errors.parentEmail && (
          <p id="parentEmail-error" className="field-error">
            {errors.parentEmail}
          </p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor="mobile">Mobile number (optional)</label>
        <input
          id="mobile"
          type="tel"
          value={values.mobile}
          onChange={(e) => updateField("mobile", e.target.value)}
          aria-invalid={Boolean(errors.mobile)}
          aria-describedby={errors.mobile ? "mobile-error" : undefined}
        />
        {errors.mobile && (
          <p id="mobile-error" className="field-error">
            {errors.mobile}
          </p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor="teenCount"># of teens attending *</label>
        <input
          id="teenCount"
          type="number"
          min="1"
          max="10"
          required
          value={values.teenCount}
          onChange={(e) => updateField("teenCount", e.target.value)}
          aria-invalid={Boolean(errors.teenCount)}
          aria-describedby={errors.teenCount ? "teenCount-error" : undefined}
        />
        {errors.teenCount && (
          <p id="teenCount-error" className="field-error">
            {errors.teenCount}
          </p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor="gradeRange">Grade range (optional)</label>
        <input
          id="gradeRange"
          type="text"
          placeholder="e.g. 9th-11th"
          value={values.gradeRange}
          onChange={(e) => updateField("gradeRange", e.target.value)}
        />
      </div>

      <div className="form-field">
        <label htmlFor="note">Note for the organizer (optional)</label>
        <textarea
          id="note"
          maxLength={MAX_NOTE_LENGTH}
          value={values.note}
          onChange={(e) => updateField("note", e.target.value)}
          aria-describedby="note-hint"
        />
        <p id="note-hint" className="field-hint">
          {values.note.length}/{MAX_NOTE_LENGTH} characters
        </p>
      </div>

      {/* Honeypot: hidden from sighted users and screen readers; bots often fill it in */}
      <div className="honeypot-field" aria-hidden="true">
        <label htmlFor="website">Leave this field blank</label>
        <input
          id="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => updateField("website", e.target.value)}
        />
      </div>

      <div className="checkbox-field">
        <input
          id="isAdult"
          type="checkbox"
          checked={values.isAdult}
          onChange={(e) => updateField("isAdult", e.target.checked)}
          aria-invalid={Boolean(errors.isAdult)}
        />
        <label htmlFor="isAdult">I am an adult parent/guardian submitting this RSVP. *</label>
      </div>
      {errors.isAdult && <p className="field-error">{errors.isAdult}</p>}

      <div className="checkbox-field">
        <input
          id="agreesToConduct"
          type="checkbox"
          checked={values.agreesToConduct}
          onChange={(e) => updateField("agreesToConduct", e.target.checked)}
        />
        <label htmlFor="agreesToConduct">
          I have read and agree to the <a href="/safety">Safety &amp; Code of Conduct</a>. *
        </label>
      </div>
      {errors.agreesToConduct && <p className="field-error">{errors.agreesToConduct}</p>}

      <div className="checkbox-field">
        <input
          id="agreesToPrivacy"
          type="checkbox"
          checked={values.agreesToPrivacy}
          onChange={(e) => updateField("agreesToPrivacy", e.target.checked)}
        />
        <label htmlFor="agreesToPrivacy">
          I have read the <a href="/privacy">Privacy Policy</a>. *
        </label>
      </div>
      {errors.agreesToPrivacy && <p className="field-error">{errors.agreesToPrivacy}</p>}

      <div className="checkbox-field">
        <input
          id="agreesToTerms"
          type="checkbox"
          checked={values.agreesToTerms}
          onChange={(e) => updateField("agreesToTerms", e.target.checked)}
        />
        <label htmlFor="agreesToTerms">
          I agree to the <a href="/terms">Terms of Use</a>. *
        </label>
      </div>
      {errors.agreesToTerms && <p className="field-error">{errors.agreesToTerms}</p>}

      <div className="form-field">
        <CaptchaWidget onToken={setCaptchaToken} resetKey={captchaReset} />
        {errors.captcha && <p className="field-error">{errors.captcha}</p>}
      </div>

      {submitError && (
        <div className="alert alert-error" role="alert">
          {submitError}
        </div>
      )}

      <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
        {submitting ? "Submitting\u2026" : "Submit RSVP"}
      </button>
    </form>
  );
}
